import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { PoiHistory, PoiHistoryStats } from '../class/PoiHistory';
import { LANDMARK_HISTORY } from '../component/management-tab/landmarks-sub-tab/landmarks-routing.constants';
import { LandmarksViews } from '../component/management-tab/landmarks-sub-tab/landmarks.routing.module';
import { Landmark } from '../database/models';
import { DateHelper } from '../utils/DateHelper';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { BaseService } from './base.service';
import { DevicesManagerService } from './devices-manager.service';
import { SnackBarService } from './snack-bar.service';
import { ViewService } from './view.service';

interface ParamsBase {
  token: string;
  from: number;
  to: number;
}

interface ParamsWithLandmark extends ParamsBase {
  uid_poi: Landmark['uid'];
  kind: Landmark['kind'];
  radius?: Landmark['radius'];
  lon?: never;
  lat?: never;
}

interface ParamsWithCoords extends ParamsBase {
  lon: Landmark['longitude'];
  lat: Landmark['latitude'];
  radius: Landmark['radius'];
  uid_poi?: never;
  kind?: never;
}

type ParamsLandmark = ParamsWithLandmark | ParamsWithCoords;

@Injectable({
  providedIn: 'root',
})
export class PoiHistoryService extends BaseService<object> {
  public showMultiDate = false;
  public poi: Landmark;
  public totalAverages = 0;
  public totalEngineAverages = 0;
  public totalEngine = 0;
  public totalMin = 0;
  public totalMax = 0;
  public radius: number;
  public interval: number;
  public todayDate: number = new Date().getTime();

  public $poisHistory = signal<PoiHistory[] | null>(null);
  public startDate = signal<number>(new DateHelper().startOfDay().getTimestamp());
  public endDate = signal<number>(new DateHelper().endOfDay().getTimestamp());

  devicesSet = new Set<number>();

  public countDevices = computed(() => {
    const devicesSet = new Set<number>();
    this.$poisHistory()?.forEach(history => {
      devicesSet.add(history.uid_device);
    });
    return Array.from(devicesSet).length;
  });

  public totalDistancesTraveled = computed(() => {
    return this.$poisHistory()?.reduce((sum, history) => sum + history.distance_traveled, 0) ?? 0;
  });

  public moyenneDistanceTraveled = computed(() => {
    const count = this.$poisHistory()?.length ?? 0;
    return count > 0 ? this.totalDistancesTraveled() / count : 0;
  });

  public firstStartDay = computed(() => {
    return this.$poisHistory()?.reduce((min, history) => Math.min(min, history.start), Infinity) ?? Infinity;
  });

  public lastStopDay = computed(() => {
    return this.$poisHistory()?.reduce((max, history) => Math.max(max, history.stop), 0) ?? 0;
  });

  public timeRangeDay = computed(() => {
    return this.lastStopDay() - this.firstStartDay();
  });

  public averageDurationStop = computed(() => {
    return this.durationStops() / this.$poisHistory()?.length;
  });

  public durationStops = computed(() => {
    return this.$poisHistory()?.reduce((sum, history) => sum + history.stopDuration, 0) ?? 0;
  });

  public durationTrips = computed(() => {
    return this.$poisHistory()?.reduce((sum, history) => sum + history.duration, 0) ?? 0;
  });

  // A mettre dans metric
  public distanceCount = computed(() => {
    return this.$poisHistory()?.reduce<number[]>((acc, history, index) => {
      const previousSum = index === 0 ? 0 : acc[index - 1];
      acc.push(previousSum + history.distance_traveled);
      return acc;
    }, []);
  });

  public minDistanceTraveled = computed(() => {
    return (
      this.$poisHistory()?.reduce((min, history) => Math.min(min, history.distance_traveled), Infinity) ?? 0
    );
  });
  public maxDistanceTraveled = computed(() => {
    return (
      this.$poisHistory()?.reduce((max, history) => Math.max(max, history.distance_traveled), -Infinity) ?? 0
    );
  });

  private _devicesManagerService = inject(DevicesManagerService);
  private _authService = inject(AuthService);
  private router = inject(Router);
  private snackBarService = inject(SnackBarService);
  private _viewService = inject(ViewService<LandmarksViews>);

  constructor() {
    super(inject(ApiService));
  }

  public override HandleResponse(response: JSON): object {
    const responseData = response['tripend'].data;
    const tempArray = [];
    for (const item of responseData) {
      const poiHistory = new PoiHistory(item);
      poiHistory.uid_poi = response['tripend'].uid_poi;
      // Ensure next_start is defined
      // If next_start is not provided, set it to the end date of range or current time
      if (!poiHistory.next_start) {
        if (this.endDate() > Math.floor(new Date().getTime() / 1000)) {
          poiHistory.next_start = Math.floor(new Date().getTime() / 1000);
        } else {
          poiHistory.next_start = this.endDate();
        }
      }
      tempArray.push(poiHistory);
    }
    return tempArray;
  }

  private _computeParams(data: {
    poi?: Landmark;
    radius?: number;
    lat?: number;
    long?: number;
  }): ParamsLandmark {
    const baseParams: ParamsBase = {
      token: this._authService.$token(),
      from: this.startDate(),
      to: this.endDate(),
    };
    if (data.poi) {
      return {
        ...baseParams,
        uid_poi: data.poi.uid,
        kind: data.poi.kind,
        radius: !data.poi.isPoly ? this.radius : undefined,
      };
    } else {
      return {
        ...baseParams,
        lon: data.long,
        lat: data.lat,
        radius: this.radius,
      };
    }
  }

  private _handleError(error: unknown) {
    this.snackBarService.send({
      message: 'HISTORY.LANDMARK.FETCH_ERROR',
      type: 'error',
    });
    console.error('Error fetching POI history data:', error);
  }

  private _computeStats(dataResult: PoiHistory[]) {
    // ---- Stat non utilisé !! -----
    // Beaucoup de chose a revoir ou retirer dans cette partie
    const dataStats: Record<number, PoiHistoryStats> = {};
    const stats = new Map<number, PoiHistoryStats>();

    for (const i in dataResult) {
      const deviceUid = dataResult[i].uid_device;

      if (!dataStats[deviceUid]) {
        dataStats[deviceUid] = new PoiHistoryStats();
        dataStats[deviceUid].maxStop = dataResult[i].next_start - dataResult[i].stop;
        dataStats[deviceUid].minStop = dataResult[i].next_start - dataResult[i].stop;
        dataStats[deviceUid].lastStart = dataResult[i].next_start;
        dataStats[deviceUid].lastStop = dataResult[i].stop;
      } else {
        if (dataStats[deviceUid].maxStop < dataResult[i].next_start - dataResult[i].stop)
          dataStats[deviceUid].maxStop = dataResult[i].next_start - dataResult[i].stop;
        if (dataStats[deviceUid].minStop > dataResult[i].next_start - dataResult[i].stop)
          dataStats[deviceUid].minStop = dataResult[i].next_start - dataResult[i].stop;
        if (dataStats[deviceUid].lastStart < dataResult[i].next_start)
          dataStats[deviceUid].lastStart = dataResult[i].next_start;
        if (dataStats[deviceUid].lastStop < dataResult[i].stop)
          dataStats[deviceUid].lastStop = dataResult[i].stop;
      }
      dataStats[deviceUid].isThere = !dataResult[i].next_start;
      dataStats[deviceUid].timeVisited++;
      dataStats[deviceUid].engine += dataResult[i].engine;
      dataStats[deviceUid].timeSpent += dataResult[i].next_start - dataResult[i].stop;
    }

    return dataStats;
  }

  private _handleComplete(
    dataResult: PoiHistory[],
    devicesList: Record<number, PoiHistoryStats>,
    devicesListBtp: Record<number, PoiHistoryStats>
  ): object {
    this.$poisHistory.set(dataResult);
    if (dataResult.length === 0) return;

    const dataStats = this._computeStats(dataResult);

    for (let i in dataStats) {
      dataStats[i].averageTimeSpent += dataStats[i].timeSpent;
      this._devicesManagerService.get(i).device_type === 0
        ? (devicesList[i] = dataStats[i])
        : (devicesListBtp[i] = dataStats[i]);

      if (devicesList[i]) {
        devicesList[i].average = dataStats[i].averageTimeSpent;
        devicesList[i].timeVisited = dataStats[i].timeVisited;
        devicesList[i].lastStart = dataStats[i].lastStart;
        devicesList[i].lastStop = dataStats[i].lastStop;
        this.totalAverages += dataStats[i].averageTimeSpent;
      } else {
        devicesListBtp[i].average = dataStats[i].averageTimeSpent;
        devicesListBtp[i].timeVisited = dataStats[i].timeVisited;
        devicesListBtp[i].averageEngine = dataStats[i].engine / dataStats[i].timeVisited;
        devicesListBtp[i].totalEngineAverages = dataStats[i].engine / this.interval;
        this.totalEngineAverages += dataStats[i].engine;
      }
      if (dataStats[i].averageTimeSpent < this.totalMax) this.totalMax = dataStats[i].averageTimeSpent;
      if (dataStats[i].averageTimeSpent > this.totalMin) this.totalMin = dataStats[i].averageTimeSpent;
      this.totalEngine += dataStats[i].engine;
    }
    dataResult.length > 0
      ? (this.totalAverages = this.totalAverages / dataResult.length)
      : (this.totalAverages = 0); //TODO length stops
    devicesListBtp && Object.keys(devicesListBtp).length > 0
      ? (this.totalEngineAverages = this.totalEngineAverages / Object.keys(devicesListBtp).length)
      : (this.totalEngineAverages = 0);

    // ---- END Stat non utilisé !! -----
  }

  public getStats(data: { poi?: Landmark; radius?: number; lat?: number; long?: number }) {
    let dataResult: PoiHistory[];
    const devicesList: Record<number, PoiHistoryStats> = {};
    const devicesListBtp: Record<number, PoiHistoryStats> = {};

    this.clear();
    this.radius = Number(data.radius);
    this.interval = Math.round((this.endDate() - this.startDate()) / 86400);
    const params: ParamsLandmark = this._computeParams(data);

    this.Get('tripend', params).subscribe(
      response => (dataResult = response as PoiHistory[]),
      error => this._handleError(error),
      () => this._handleComplete(dataResult, devicesList, devicesListBtp)
    );
    this.checkIfPeriodMultidate(this.startDate(), this.endDate());
  }

  private checkIfPeriodMultidate(from: number, to: number) {
    const start = new Date(from * 1000);
    const end = new Date(to * 1000);
    const startNumber = `${start.getDate()}-${start.getMonth()}`;
    const endNumber = `${end.getDate()}-${end.getMonth()}`;
    this.showMultiDate = startNumber !== endNumber;
  }

  private clear() {
    this.totalAverages = 0;
    this.totalEngineAverages = 0;
    this.totalEngine = 0;
    this.totalMax = 0;
    this.totalMax = 0;
  }

  public sortByKey(array, key) {
    return array.sort((a, b) => {
      const x = a[key];
      const y = b[key];
      if (x < y) {
        return -1;
      }
      if (x > y) {
        return 1;
      }
      return 0;
    });
  }

  public tryGo(poi: Landmark, from: number, to: number, radius: number) {
    const startDate = new DateHelper(from);
    const endDate = new DateHelper(to);
    const tomorrow = new DateHelper().addDays(1);
    const endDateFinish = new DateHelper(new DateHelper(to).setHours(23, 59, 59, 0));

    if (startDate > tomorrow || startDate.timestamp() > endDate.timestamp() || endDate > tomorrow) {
      return this.snackBarService.send({
        message: 'SNACK_BAR.INVALID_DATE',
        type: 'error',
      });
    } else {
      // garde fou pour eviter des requetes trop grosse // amplitude de deux mois
      const testPeriod = new DateHelper(new DateHelper(startDate).setHours(23, 59, 59, 0)).addMonths(2);
      if (endDateFinish > testPeriod) {
        this.snackBarService.send({
          message: 'SNACK_BAR.POI_HISTORY',
          type: 'error',
        });
        return;
      } else {
        this.router.navigate([LANDMARK_HISTORY(poi.uid_parent, poi.uid)], {
          queryParams: {
            from: from,
            to: to,
            radius: radius,
            view: this._viewService.currentView(),
          },
        });
      }
    }
  }

  public restrictRadius(radius: number) {
    if (radius < 0) {
      return 0.03;
    }

    if (radius > 10) {
      return 10;
    }

    return radius;
  }
}
